import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { log } from 'evlog/client';
import {
  Calendar,
  FileText,
  LogOut,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Shield,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import type { GetCurrentUser } from '@virtual-care/client';
import {
  displayName,
  formatDateShort,
  formatPatientMid,
  toAge,
} from '@virtual-care/isomorphic';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
} from '@virtual-care/shadcn';
import {
  FieldMessage,
  FormMessage,
  GenderField,
  Loading,
  WhatsappLink,
  errorToFormIssue,
  useAction,
} from '@virtual-care/ui';

import defaultAvatar from '../../assets/profile.png';
import { Header } from '../components/header';
import { logout, useUser } from '../firebase';
import { useSettingValue } from '../hooks/use-settings';

const profileSchema = z.object({
  firstName: z.string().min(1, { message: 'shared.validation.nameRequired' }),
  lastName: z.string().min(1, { message: 'shared.validation.nameRequired' }),
  parentName: z.string().optional(),
  phone: z.string().min(1, { message: 'shared.validation.phoneRequired' }),
  email: z.string().email({ message: 'shared.validation.emailInvalid' }),
  nationalId: z.string().optional(),
  insuranceCompany: z.string().optional(),
  residency: z.string().optional(),
  gender: z.string(),
});

function B2CProfile({
  user: data,
}: {
  user: Omit<GetCurrentUser, 'b2bProfile'> & {
    b2cProfile: NonNullable<GetCurrentUser['b2cProfile']>;
  };
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const termsAndPrivacyUrl = useSettingValue('termsAndConditionsUrl');
  const supportPhoneNumber = useSettingValue('supportPhoneNumber');

  const username = displayName(data);
  const genderLabel =
    data.b2cProfile.gender === 'male' || data.b2cProfile.gender === 'female'
      ? t(`common.${data.b2cProfile.gender}`)
      : '';
  const age = toAge(data.b2cProfile);
  const residency = data.b2cProfile.residency || t('common.notSpecified');
  const mid = data.mid ? formatPatientMid(data.mid) : t('common.notSpecified');

  const { mutateAsync, isPending } = useAction('PATCH /profile', {
    invalidate: ['GET /patients/me'],
  });

  const form = useForm({
    defaultValues: {
      firstName: data?.b2cProfile.firstName,
      lastName: data?.b2cProfile.lastName,
      parentName: data?.b2cProfile.parentName,
      phone: data?.phoneNumber,
      email: data.email,
      nationalId: data?.b2cProfile.nationalId,
      insuranceCompany: data?.b2cProfile?.insurance?.insuranceCompany,
      residency: data?.b2cProfile.residency || '',
      gender: data?.b2cProfile.gender,
    } as z.infer<typeof profileSchema>,
    validators: {
      onSubmit: profileSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          log.info({ action: 'update_profile' });
          await mutateAsync(
            {
              firstName: value.firstName,
              lastName: value.lastName,
              parentName: value.parentName || '',
              birthDate: data?.b2cProfile?.birthDate || '',
              nationalId: value.nationalId,
              residency: value.residency || '',
              gender: value.gender,
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['user'] });
                toast.success(t('profilePage.updateSuccess'));
              },
            },
          );
          return;
        } catch (error) {
          log.error({ action: 'update_profile', error });
          return errorToFormIssue(error);
        }
      },
    },
  });

  if (isPending) return <Loading />;

  return (
    <div className="mb-4">
      <Header
        title={
          <h1 className="text-2xl font-bold text-gray-900">
            {t('profilePage.pageTitle')}
          </h1>
        }
      />

      <div className="mx-auto max-w-4xl px-4 py-6">
        <div className="space-y-6">
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col space-y-4 md:hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-16 w-16 rounded-lg">
                      <AvatarImage
                        src={
                          defaultAvatar ||
                          `https://api.dicebear.com/5.x/initials/svg?seed=${username}`
                        }
                        alt={username}
                        className="object-cover"
                      />
                      <AvatarFallback>{username}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {username}
                      </h2>
                      <span className="text-sm text-gray-500">{mid}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    onClick={async () => {
                      await logout();
                      await navigate('/signin');
                    }}
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {t('profilePage.logout')}
                    </span>
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Calendar className="h-4 w-4 flex-shrink-0" />
                    <span>
                      {t('profilePage.ageLabel', {
                        value: age ?? t('common.notSpecified'),
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    <span>
                      {t('profilePage.residencyLabel', { value: residency })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <User className="h-4 w-4 flex-shrink-0" />
                    <span>
                      {t('profilePage.genderLabel', { value: genderLabel })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="hidden md:flex md:items-center md:justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="h-24 w-24 rounded-lg lg:h-[140px] lg:w-[140px]">
                    <AvatarImage
                      src={
                        defaultAvatar ||
                        `https://api.dicebear.com/5.x/initials/svg?seed=${username}`
                      }
                      alt={username}
                      className="object-cover"
                    />
                    <AvatarFallback>{username}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col space-y-2">
                    <h2 className="text-xl font-semibold text-gray-900 lg:text-2xl">
                      {username}
                    </h2>
                    <span className="text-sm text-gray-500">{mid}</span>
                    <div className="flex items-center gap-2 text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span className="text-sm">
                        {t('profilePage.ageLabel', {
                          value: age ?? t('common.notSpecified'),
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-500">
                      <MapPin className="h-4 w-4" />
                      <span className="text-sm">
                        {t('profilePage.residencyLabel', { value: residency })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-500">
                      <User className="h-4 w-4" />
                      <span className="text-sm">
                        {t('profilePage.genderLabel', { value: genderLabel })}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="flex items-center gap-2 self-end"
                  onClick={async () => {
                    await logout();
                    await navigate('/signin');
                  }}
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t('profilePage.logout')}</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>{t('profilePage.personalInfo')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form
                className="space-y-6"
                onSubmit={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await form.handleSubmit();
                }}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <form.Field
                    name="firstName"
                    children={(field) => (
                      <div className="space-y-2">
                        <Label
                          htmlFor="firstName"
                          className="text-sm font-medium"
                        >
                          {t('profilePage.firstName')}
                        </Label>
                        <Input
                          id="firstName"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          className="bg-gray-50"
                        />
                        <FieldMessage errors={field.state.meta.errors} />
                      </div>
                    )}
                  />

                  <form.Field
                    name="parentName"
                    children={(field) => (
                      <div className="space-y-2">
                        <Label
                          htmlFor="parentName"
                          className="text-sm font-medium"
                        >
                          {t('profilePage.parentName')}
                        </Label>
                        <Input
                          id="parentName"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          className="bg-gray-50"
                        />
                        <FieldMessage errors={field.state.meta.errors} />
                      </div>
                    )}
                  />

                  <form.Field
                    name="lastName"
                    children={(field) => (
                      <div className="space-y-2">
                        <Label
                          htmlFor="lastName"
                          className="text-sm font-medium"
                        >
                          {t('profilePage.lastName')}
                        </Label>
                        <Input
                          id="lastName"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          className="bg-gray-50"
                        />
                        <FieldMessage errors={field.state.meta.errors} />
                      </div>
                    )}
                  />

                  <form.Field
                    name="residency"
                    children={(field) => (
                      <div className="space-y-2">
                        <Label
                          htmlFor="residency"
                          className="text-sm font-medium"
                        >
                          {t('profilePage.residency')}
                        </Label>
                        <div className="relative">
                          <MapPin className="absolute top-3 h-4 w-4 text-gray-400 ltr:left-3 rtl:right-3" />
                          <Input
                            id="residency"
                            value={field.state.value || ''}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                            className="bg-gray-50 ltr:pl-10 rtl:pr-10"
                          />
                        </div>
                        <FieldMessage errors={field.state.meta.errors} />
                      </div>
                    )}
                  />
                </div>

                <Separator className="my-9" />
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <form.Field
                    name="phone"
                    children={(field) => (
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium">
                          {t('profilePage.phone')}
                        </Label>
                        <div className="relative">
                          <Phone className="absolute top-3 h-4 w-4 text-gray-400 ltr:left-3 rtl:right-3" />
                          <Input
                            id="phone"
                            disabled={true}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                            className="bg-gray-50 ltr:pl-10 rtl:pr-10"
                            dir="ltr"
                          />
                        </div>
                        <FieldMessage errors={field.state.meta.errors} />
                      </div>
                    )}
                  />

                  <form.Field
                    name="email"
                    children={(field) => (
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium">
                          {t('profilePage.email')}
                        </Label>
                        <div className="relative">
                          <Mail className="absolute top-3 h-4 w-4 text-gray-400 ltr:left-3 rtl:right-3" />
                          <Input
                            id="email"
                            type="email"
                            autoComplete="email"
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                            className="bg-gray-50 ltr:pl-10 rtl:pr-10"
                          />
                        </div>
                        <FieldMessage errors={field.state.meta.errors} />
                      </div>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <form.Field
                    name="nationalId"
                    children={(field) => (
                      <div className="space-y-2">
                        <Label
                          htmlFor="nationalId"
                          className="text-sm font-medium"
                        >
                          {t('profilePage.nationalIdWithInsurance')}
                        </Label>
                        <Input
                          id="nationalId"
                          disabled={true}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          className="bg-gray-50"
                        />
                        <FieldMessage errors={field.state.meta.errors} />
                      </div>
                    )}
                  />

                  <form.Field
                    name="insuranceCompany"
                    children={(field) => (
                      <div className="space-y-2">
                        <Label
                          htmlFor="insuranceCompany"
                          className="text-sm font-medium"
                        >
                          {t('profilePage.insuranceCompany')}
                        </Label>
                        <div className="relative">
                          <Shield className="absolute top-3 h-4 w-4 text-gray-400 ltr:left-3 rtl:right-3" />
                          <Input
                            disabled
                            id="insuranceCompany"
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                            className="bg-gray-50 ltr:pl-10 rtl:pr-10"
                          />
                        </div>
                        <FieldMessage errors={field.state.meta.errors} />
                      </div>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-2">
                  <form.Field
                    name="gender"
                    children={(field) => (
                      <GenderField
                        field={field}
                        label={t('common.gender')}
                        placeholder={t('common.select')}
                        maleLabel={t('common.male')}
                        femaleLabel={t('common.female')}
                      />
                    )}
                  />
                  <form.Subscribe
                    selector={(state) => [state.canSubmit, state.isSubmitting]}
                    children={([canSubmit, isSubmitting]) => (
                      <Button
                        type="submit"
                        disabled={!canSubmit || isSubmitting}
                        className="bg-primary hover:bg-primary/90 w-36"
                      >
                        {isSubmitting
                          ? t('profilePage.saving')
                          : t('profilePage.save')}
                      </Button>
                    )}
                  />
                </div>

                <form.Subscribe
                  selector={(state) => state.errors}
                  children={(errors) => <FormMessage errors={errors} />}
                />
              </form>
            </CardContent>
          </Card>

          <FooterLinks
            termsUrl={termsAndPrivacyUrl}
            supportPhoneNumber={supportPhoneNumber}
          />
        </div>
      </div>
    </div>
  );
}

function B2BProfile({
  user,
}: {
  user: Omit<GetCurrentUser, 'b2cProfile'> & {
    b2bProfile: NonNullable<GetCurrentUser['b2bProfile']>;
  };
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const termsAndPrivacyUrl = useSettingValue('termsAndConditionsUrl');
  const supportPhoneNumber = useSettingValue('supportPhoneNumber');

  const username = displayName(user);
  const mid = user.mid ? formatPatientMid(user.mid) : t('common.notSpecified');
  const createdAt = user.createdAt
    ? formatDateShort(user.createdAt)
    : t('common.notSpecified');

  return (
    <div className="mb-4">
      <Header
        title={
          <h1 className="text-2xl font-bold text-gray-900">
            {t('profilePage.pageTitle')}
          </h1>
        }
      />

      <div className="mx-auto max-w-4xl px-4 py-6">
        <div className="space-y-6">
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col space-y-4 md:hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-16 w-16 rounded-lg">
                      <AvatarImage
                        src={
                          defaultAvatar ||
                          `https://api.dicebear.com/5.x/initials/svg?seed=${username}`
                        }
                        alt={username}
                        className="object-cover"
                      />
                      <AvatarFallback>{username}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {username}
                      </h2>
                      <span className="text-sm text-gray-500">{mid}</span>
                      <div className="flex items-center gap-2 text-sm text-blue-600">
                        <Shield className="h-4 w-4" />
                        <span>{t('profilePage.organizationAccount')}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    onClick={async () => {
                      await logout();
                      await navigate('/signin');
                    }}
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {t('profilePage.logout')}
                    </span>
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <span>
                      {t('profilePage.phoneNumberLine', {
                        value: user.phoneNumber,
                      })}
                    </span>
                  </div>
                  {user.email && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      <span>
                        {t('profilePage.emailLine', { value: user.email })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="hidden md:flex md:items-center md:justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="h-24 w-24 rounded-lg lg:h-[140px] lg:w-[140px]">
                    <AvatarImage
                      src={
                        defaultAvatar ||
                        `https://api.dicebear.com/5.x/initials/svg?seed=${username}`
                      }
                      alt={username}
                      className="object-cover"
                    />
                    <AvatarFallback>{username}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col space-y-2">
                    <h2 className="text-xl font-semibold text-gray-900 lg:text-2xl">
                      {username}
                    </h2>
                    <span className="text-sm text-gray-500">{mid}</span>
                    <div className="flex items-center gap-2 text-blue-600">
                      <Shield className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {t('profilePage.organizationAccount')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-500">
                      <Phone className="h-4 w-4" />
                      <span className="text-sm">
                        {t('profilePage.phoneNumberLine', {
                          value: user.phoneNumber,
                        })}
                      </span>
                    </div>
                    {user.email && (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Mail className="h-4 w-4" />
                        <span className="text-sm">
                          {t('profilePage.emailLine', { value: user.email })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="flex items-center gap-2 self-end"
                  onClick={async () => {
                    await logout();
                    await navigate('/signin');
                  }}
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t('profilePage.logout')}</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>{t('profilePage.accountInfo')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    {t('profilePage.organizationName')}
                  </Label>
                  <div className="flex items-center gap-2 rounded-md bg-gray-50 p-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">{username}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    {t('profilePage.phoneNumber')}
                  </Label>
                  <div className="flex items-center gap-2 rounded-md bg-gray-50 p-3">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900" dir="ltr">
                      {user.phoneNumber}
                    </span>
                  </div>
                </div>

                {user.email && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      {t('profilePage.email')}
                    </Label>
                    <div className="flex items-center gap-2 rounded-md bg-gray-50 p-3">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900">{user.email}</span>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    {t('profilePage.accountType')}
                  </Label>
                  <div className="flex items-center gap-2 rounded-md bg-blue-50 p-3">
                    <Shield className="h-4 w-4 text-blue-500" />
                    <span className="font-medium text-blue-700">
                      {t('profilePage.organizationAccount')}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    {t('profilePage.createdAt')}
                  </Label>
                  <div className="flex items-center gap-2 rounded-md bg-gray-50 p-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">{createdAt}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <FooterLinks
            termsUrl={termsAndPrivacyUrl}
            supportPhoneNumber={supportPhoneNumber}
          />
        </div>
      </div>
    </div>
  );
}

function FooterLinks({
  termsUrl,
  supportPhoneNumber,
}: {
  termsUrl?: string;
  supportPhoneNumber?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <a
        href={termsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block"
      >
        <Card className="cursor-pointer transition-colors hover:bg-gray-50">
          <CardContent className="px-4">
            <div className="flex items-center space-x-2">
              <div className="bg-secondary rounded-full p-2">
                <FileText className="text-secondary-foreground h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium">
                  {t('profilePage.termsLink')}
                </p>
                <p className="text-xs text-gray-500">
                  {t('profilePage.termsDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </a>

      <WhatsappLink phone={supportPhoneNumber} className="block">
        <Card className="cursor-pointer transition-colors hover:bg-gray-50">
          <CardContent className="px-4">
            <div className="flex items-center space-x-2">
              <div className="bg-secondary rounded-full p-2">
                <MessageCircle className="text-secondary-foreground h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium">
                  {t('profilePage.contactUsWhatsapp')}
                </p>
                <p className="text-xs text-gray-500">
                  {t('profilePage.contactUsWhatsappDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </WhatsappLink>
    </div>
  );
}

export default function Profile() {
  const { data } = useUser();
  if (!data) return null;
  if (data.b2cProfile) {
    return <B2CProfile user={data} />;
  } else {
    return <B2BProfile user={data} />;
  }
}
